import settings from '@tenant/settings'

/**
 * List of keys that define a customer role
 * @type {string[]}
 */
const customerRoleKeys = [
  'Customer',
  'Requester',
]

/**
 * User role type definition
 *
 * @typedef {object} userRole
 * @property {string} value                 the readable string of the user role
 * @property {string} key                   the role key
 * @property {boolean} hasMessages          whether this role can send/receive messages
 * @property {boolean} hasTeams             whether this role can have teams
 * @property {boolean} [canBeMentioned]     whether this role can be mentioned using {@link Mention}
 * @property {boolean} [canBeInvited]       whether this role is an acceptable invitation role
 * @property {boolean} [canBePublishedTo]   whether a request can be published to this role if
 *                                          `true`, checkbox "Publish to customer" in ticket wizard
 *                                          will be shown
 */

/**
 * User roles collection
 *
 * Contains a list of possible roles a user can have.
 *
 * @type {userRole[]} userRoles
 */
const userRoles = settings.roles
  || [
    {
      value: 'Waiting for approval',
      key: 'Requester',
      canBePublishedTo: true,
      canBeInvited: true,
      hasTeams: false,
      subscription: {
        message: true,
      },
    },
    {
      value: 'Verified customer',
      key: 'Customer',
      canBePublishedTo: true,
      canBeInvited: true,
      hasTeams: false,
      subscription: {
        message: true,
      },
    },
    {
      value: 'Blocked',
      key: 'Blocked',
      canBeMentioned: false,
      canBeInvited: false,
      hasTeams: false,
      subscription: {
        message: false,
      },
    },
    {
      value: 'Customer Service Agent',
      key: 'CustomerServiceAgent',
      canBeMentioned: true,
      canBeInvited: true,
      hasTeams: true,
      subscription: {
        message: true,
      },
    },
    {
      value: 'Customer Service Manager',
      key: 'CustomerServiceManager',
      canBeMentioned: true,
      canBeInvited: true,
      hasTeams: true,
      subscription: {
        message: true,
      },
    },
    {
      value: 'Customer Service Representative',
      key: 'CustomerServiceRepresentative',
      canBeMentioned: true,
      canBeInvited: true,
      hasTeams: true,
      subscription: {
        message: true,
      },
    },
    {
      value: 'Field Service Engineer',
      key: 'FieldServiceEngineer',
      canBePublishedTo: true,
      canBeInvited: true,
      hasTeams: false,
      subscription: {
        message: true,
      },
    },
    {
      value: 'Customer Service External',
      key: 'CustomerServiceExternal',
      hasTeams: false,
      subscription: {
        message: false,
      },
    },
    {
      value: 'Customer Service Cooperator',
      key: 'CustomerServiceCooperator',
      canBePublishedTo: true,
      canBeInvited: true,
      hasTeams: false,
      subscription: {
        message: false,
      },
    },
    {
      value: 'Sales',
      key: 'Sales',
      canBePublishedTo: true,
      canBeMentioned: true,
      canBeInvited: true,
      hasTeams: true,
      subscription: {
        message: true,
      },
    },
    {
      value: 'Customer Service Expert',
      key: 'CustomerServiceExpert',
      hasTeams: true,
      subscription: {
        message: true,
      },
    },
    {
      value: 'Master Data Administrator',
      key: 'MasterDataAdministrator',
      hasTeams: true,
      subscription: {
        message: true,
      },
    },
    {
      value: 'Instance Administrator',
      key: 'InstanceAdministrator',
      subscription: {
        message: true,
      },
    },
    {
      value: 'Technical Documentation and Parts',
      key: 'TechdocPartsReader',
      canBePublishedTo: false,
      canBeInvited: false,
      hasTeams: false,
      subscription: {
        message: false,
      },
    },
    {
      value: 'Technical Documentation and Parts Admin',
      key: 'TechdocPartsAdmin',
      canBePublishedTo: false,
      canBeInvited: false,
      hasTeams: false,
      subscription: {
        message: false,
      },
    },
    {
      value: 'Account In Shop',
      key: 'OrderInShop',
      canBePublishedTo: false,
      canBeInvited: false,
      hasTeams: false,
      subscription: {
        message: false,
      },
    },
    {
      value: 'PIN Creator',
      key: 'PinCreatorOnly_c',
      canBePublishedTo: false,
      canBeInvited: false,
      hasTeams: false,
      subscription: {
        message: false,
      },
    },
  ]

/**
 * Composite roles collection
 *
 * A list of role that define a composition of other {@link userRole}s.
 * @type {userRole[]} composite roles
 */
const compositeRoles = [
  {
    value: 'Customer',
    key: 'Customers',
    hasMessages: false,
    hasTeams: false,
  },
  {
    value: 'Customer service',
    key: 'CustomerService',
    hasMessages: false,
    hasTeams: false,
  },
]

/**
 * A list of {@link userRole} `key`s that are considered fundamental, meaning they can't
 * be further composed as opposed to {@link compositeRoles}.
 *
 * @type {string[]}
 */
const fundamentalRoles = [
  'FieldServiceEngineer',
  'CustomerServiceRepresentative',
  'CustomerServiceExternal',
  'CustomerServiceCooperator',
  'Sales',
]

/**
 * Reduced Roles Collection
 *
 * A list of reduced {@link userRole}s that consist of the concatenation
 * of all {@link fundamentalRoles} and {@link compositeRoles}.
 *
 * @type {userRole[]}
 */
export const reducedRoles = compositeRoles
  .concat(userRoles.filter(role => fundamentalRoles.includes(role.key)))

/**
 * Requester roles collection
 *
 * Contains a list of possible roles a Requester/Customer can have.
 *
 * @typedef {userRole[]} userRoles
 */
export const customerRoles = userRoles.filter(role => customerRoleKeys.includes(role.key))

export default userRoles
